// At-risk and LARDO helpers shared by the admin and teacher dashboards.

export const JHS_GRADES = [7, 8, 9, 10];
export const SHS_GRADES = [11, 12];

// 7 from "Grade 7", "7", 7, "G7"…
export function gradeNumber(gradeLevel) {
  const match = String(gradeLevel ?? '').match(/\d+/);
  return match ? Number(match[0]) : null;
}

export function initials(name) {
  return (
    String(name || '')
      .split(/\s+/)
      .filter((w) => /^[a-z]/i.test(w))
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('') || '?'
  );
}

export function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
}

// The school's risk levels, set by the server on each at-risk record
// (risk_level): Passable (75–79, needs monitoring), Moderate (below 75) and
// High (below 75 in 3 or more subjects that term). Most serious first.
export const RISK_LEVELS = [
  { level: 'High', label: 'High', variant: 'red' },
  { level: 'Moderate', label: 'Moderate', variant: 'amber' },
  { level: 'Passable', label: 'Passable', variant: 'blue' },
];
const NO_LEVEL = { level: null, label: '—', variant: 'secondary' };

// The level of an at-risk record's risk_level.
export function riskLevel(level) {
  return RISK_LEVELS.find((r) => r.level === level) ?? NO_LEVEL;
}

// The most serious of several records' levels.
function highestLevel(levels) {
  return RISK_LEVELS.find((r) => levels.includes(r.level)) ?? NO_LEVEL;
}

// At-risk rows come one per student and subject; the dashboards list one
// row per student with their low subjects, lowest grade first, at the most
// serious of their subjects' risk levels. A subject
// keeps its record (atriskID, recommendation, action taken) so a teacher
// can log what was done for it.
export function groupAtRiskByStudent(rows) {
  const byStudent = new Map();
  for (const row of rows) {
    const key = row.studentID ?? row.lrn ?? row.student_name;
    if (!byStudent.has(key)) {
      byStudent.set(key, {
        id: key,
        name: row.student_name,
        lrn: row.lrn,
        gradeLevel: row.section_grade || row.grade_level,
        grade: gradeNumber(row.section_grade || row.grade_level),
        section: row.section_name || row.room_name,
        adviser: row.adviser,
        subjects: [],
      });
    }
    byStudent.get(key).subjects.push({
      title: row.subject_title,
      grade: row.transmuted_grade,
      risk: riskLevel(row.risk_level),
      teacher: row.teacher,
      studentID: row.studentID,
      atriskID: row.atriskID,
      remarks: row.remarks,
      action_taken: row.action_taken,
    });
  }
  return [...byStudent.values()].map((s) => {
    const grades = s.subjects.map((x) => Number(x.grade)).filter((n) => !Number.isNaN(n));
    s.subjects.sort((a, b) => Number(a.grade) - Number(b.grade));
    return {
      ...s,
      lowest: grades.length ? Math.min(...grades) : null,
      risk: highestLevel(s.subjects.map((x) => x.risk.level)),
      subjectText: s.subjects.map((x) => x.title).join(', '),
    };
  });
}

// LARDO remarks start with the tier: "CRITICAL TRACKING: …", "EARLY
// WARNING: …", "CHRONIC ABSENTEEISM: …", "NO LONGER PARTICIPATING … (NLPA): …".
export function lardoSeverity(remarks) {
  const r = String(remarks || '');
  if (r.startsWith('NO LONGER PARTICIPATING')) {
    return { label: 'NLPA', variant: 'red', icon: 'mdi-alert-octagon-outline' };
  }
  if (r.startsWith('CHRONIC')) {
    return { label: 'Chronic absenteeism', variant: 'red', icon: 'mdi-alert-octagon-outline' };
  }
  if (r.startsWith('CRITICAL')) {
    return { label: 'Critical', variant: 'red', icon: 'mdi-alert-octagon-outline' };
  }
  if (r.startsWith('EARLY WARNING')) {
    return { label: 'Early warning', variant: 'amber', icon: 'mdi-alert-outline' };
  }
  return { label: 'Notice', variant: 'blue', icon: 'mdi-information-outline' };
}

// "CRITICAL: finding … Recommendation: next step" → { finding, recommendation }
export function lardoRemarkParts(remarks) {
  const r = String(remarks || '');
  const colon = r.indexOf(':');
  const body = colon > -1 ? r.slice(colon + 1).trim() : r;
  const at = body.search(/Recommendation:/i);
  if (at === -1) return { finding: body, recommendation: '' };
  return {
    finding: body.slice(0, at).trim(),
    recommendation: body.slice(at + 'Recommendation:'.length).trim(),
  };
}

// DepEd performance descriptor for a term grade, the scale on the report
// card: Advancing 90+, Benchmarking 80–89, Connecting 75–79, Developing
// 65–74, Emerging below 65. '' when there is no grade yet.
const DESCRIPTORS = [
  { min: 90, label: 'Advancing', description: 'Consistently demonstrates skills and understanding that meet or exceed standards with independence, flexibility, and depth.' },
  { min: 80, label: 'Benchmarking', description: 'Demonstrates expected grade-level skills and understanding competently and independently.' },
  { min: 75, label: 'Connecting', description: 'Demonstrates sufficient understanding and application of grade-level standards with occasional guidance and support.' },
  { min: 65, label: 'Developing', description: 'Demonstrates partial understanding and inconsistent application of skills, requires targeted support and scaffolding.' },
  { min: 0, label: 'Emerging', description: 'Does not yet demonstrate foundational skills and understanding; requires intensive support.' },
];
const descriptorOf = (grade) => {
  const n = Number(grade);
  if (grade == null || grade === '' || Number.isNaN(n)) return null;
  return DESCRIPTORS.find((d) => n >= d.min) ?? null;
};
export function gradeDescriptor(grade) {
  return descriptorOf(grade)?.label ?? '';
}
// The descriptor's general description (shown on hover), '' without a grade.
export function gradeDescriptorDescription(grade) {
  return descriptorOf(grade)?.description ?? '';
}
